/**
 * Slice 18h — the client half of the "roster changed" push.
 *
 * The live map disables its 60 s fallback poll while the socket is healthy, so a
 * vehicle that LEAVES the fleet (hidden, un-assigned, swapped away) would sit on
 * the map as a stale marker until the next reconnect — the reported bug, because
 * `applyFrame` can never add or remove a vehicle. The server now pushes a
 * `{ type: 'roster' }` event when the roster changes; this coalescer answers it
 * with exactly ONE re-fetch per trailing window, however many events arrive.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 TRAILING WINDOW, NOT A LEADING-EDGE THROTTLE.
 * ─────────────────────────────────────────────────────────────────────────────
 * A leading-edge throttle answers the FIRST event and drops the rest — losing the
 * final state, which reintroduces this exact bug under a new cause (the last hide
 * in a burst never refetched). So `notify()` ARMS a timer and the refetch fires at
 * the END of the window, capturing every event up to that point in one fetch.
 *
 * Pure and testable without a socket, a map, or TanStack Query — for the same
 * reason `applyFrame` is: the reducer that keeps the live model correct under push
 * must be unit-testable in isolation.
 */

export interface RosterRefresher {
  /** A roster event arrived: schedule a coalesced re-fetch. */
  notify(): void;
  /** Cancel any pending timer and drop the queued flag (effect cleanup). */
  dispose(): void;
}

export interface RosterRefresherOptions {
  /** Invalidate/refetch the vehicle queries. May be async; the next window will
   * not start a second refetch until this settles (see the re-arm on completion). */
  refetch: () => void | Promise<void>;
  /** Trailing-window length in ms. Real callers pass 1000 (matches the server's
   * THROTTLE_WINDOW_MS), so a server burst and a client burst coalesce alike. */
  windowMs: number;
}

export function createRosterRefresher(opts: RosterRefresherOptions): RosterRefresher {
  let queued = false;
  let running = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const arm = (): void => {
    // One timer at a time, and never while a refetch is in flight — the in-flight
    // completion re-arms if anything queued during the flight. This is what bounds
    // the refetch count to at most one per window even under a slow network.
    if (timer !== null || running) return;
    timer = setTimeout(fire, opts.windowMs);
  };

  const fire = (): void => {
    timer = null;
    if (!queued) return; // nothing accumulated this window
    queued = false;
    running = true;
    Promise.resolve()
      .then(opts.refetch)
      .catch(() => {
        // A failed refetch must not wedge the refresher. The next roster event (or
        // the 60 s fallback poll once the socket drops) will try again; swallowing
        // here keeps `running` from sticking true forever.
      })
      .finally(() => {
        running = false;
        // 🔴 If events arrived DURING the flight, arm again so the final state is
        // fetched. Without this a hide that lands mid-refetch is lost — the bug.
        if (queued) arm();
      });
  };

  return {
    notify(): void {
      queued = true;
      arm();
    },
    dispose(): void {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      queued = false;
    },
  };
}
